import React, { useEffect } from 'react'
import { useState } from 'react'
import { H3 } from '../../../layouts/TextStyles'
import { Box, LinearProgress, Tab, Tabs, Typography } from '@mui/material'
import styled from '@emotion/styled'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import AnalysisTable from './components/AnalysisTable'
import { examAnalysisApiSlice } from '../../../features/curator/examAnalysisSlice'
import { CourseExamState, ExamTypeState } from '../../../features/types'
import CircularProgressWithLabel from '../../../components/muiComponents/circularProgressWithLabel'
import { capitalize } from '../../../utils/functions'
import Sentinel from '../../../components/infiniteScroll/Sentinel'
import { usePaginatedList } from '../../../app/hooks/usePaginatedList'
import { ui } from '../../../utils/uiTokens'

const AnalysisPage = () => {
    const [searchParams, setSearchParams] = useSearchParams('')
    const { formatMessage, locale } = useIntl()
    const [fetchListOfExams, { data: listOfExams }] = examAnalysisApiSlice.useLazyFetchListOfExamsQuery()
    const [fetchExamParticipation, { data: examParticipation, isFetching: isFetchingParticipation }] = examAnalysisApiSlice.useLazyFetchExamParticipationQuery()
    const [fetchExamScoreRange, { data: examScoreRange, isFetching: isFetchingScoreRange }] = examAnalysisApiSlice.useLazyFetchExamScoreRangeQuery()
    const [fetchStudentAnalysis] = examAnalysisApiSlice.useLazyFetchStudentAnalysisQuery()
    const [examId, setExamId] = useState<number>(0)
    const [examType, setExamType] = useState<ExamTypeState>(
        (searchParams.get('type') as ExamTypeState) || 'BS',
    )

    const {
        rows,
        isLoading: isFetchingStudentAnalysis,
        loadedPages,
        sentinelRef,
    } = usePaginatedList(
        (page, size) =>
            fetchStudentAnalysis({ examId, page, size }).unwrap(),
        [examId],
        { skip: !examId },
    )

    useEffect(() => {
        fetchListOfExams()
    }, [])

    const examsOfType = listOfExams?.filter(
        (exam: CourseExamState) => (exam.type || 'BS') === examType,
    )

    // Держим выбранный экзамен внутри текущего типа: при смене типа старый id
    // отсутствует в списке и данные остались бы от предыдущей вкладки.
    useEffect(() => {
        if (!examsOfType) return

        if (examsOfType.length === 0) {
            setExamId(0)
            return
        }

        if (!examsOfType.some((exam: CourseExamState) => exam.id === examId)) {
            handleChangeExamId(examsOfType[0].id)
        }
    }, [listOfExams, examType])

    const handleChangeExamId = (id: number) => {
        setExamId(id)
        fetchExamParticipation({ examId: id })
        fetchExamScoreRange({ examId: id })
    }

    const handleChangeExamType = (type: ExamTypeState) => {
        setExamType(type)
    }

    useEffect(() => {
        setSearchParams({ type: examType })
    }, [examType])

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'examAnalysis' })}</H3>
                    </Title>
                    <Tabs sx={styles.typeGroup} variant="scrollable" scrollButtons="auto">
                        {(['BS', 'PKS'] as ExamTypeState[]).map((type) => (
                            <Tab key={type} onClick={() => handleChangeExamType(type)} sx={styles.typeButton(examType === type)}
                                label={formatMessage({ id: type === 'PKS' ? 'examTypePks' : 'examTypeBs' })} />
                        ))}
                    </Tabs>
                    <Tabs sx={styles.buttonGroup} variant="scrollable" scrollButtons="auto">
                        {examsOfType?.map((exam: CourseExamState) => (
                            <Tab key={exam.id} onClick={() => handleChangeExamId(exam.id)} sx={styles.button(examId === exam.id)}
                                label={exam[`title${capitalize(locale)}`]} />
                        ))}
                    </Tabs>
                </Header>
                {isFetchingParticipation || isFetchingScoreRange ? (
                    <LinearProgress />
                ) : (
                    <Box sx={styles.cards}>
                        <Card>
                            <CircularProgressWithLabel value={examParticipation?.passedExamPercent || 0} size={56} color={'secondary'} variant="determinate" />
                            <Box>
                                <CardLabel>{formatMessage({ id: 'passed' })}</CardLabel>
                                <CardValue>{examParticipation?.passedStudentCount || 0} {formatMessage({ id: 'student' })}</CardValue>
                                <CardHint>
                                    {formatMessage({ id: 'unpassed' })}: {examParticipation?.unPassedStudentCount || 0}
                                </CardHint>
                            </Box>
                        </Card>

                        <Card>
                            <CircularProgressWithLabel value={examParticipation?.averagePercent || 0} size={56} color={'secondary'} variant="determinate" />
                            <Box>
                                <CardLabel>{formatMessage({ id: 'averagePoint' })}</CardLabel>
                                <CardValue>{examParticipation?.averageScore || 0} балл</CardValue>
                            </Box>
                        </Card>

                        <Card>
                            <Box>
                                <CardLabel>{formatMessage({ id: 'curatorSubjectAverage' })}</CardLabel>
                                <CardValue style={{ color: ui.pink }}>
                                    {examParticipation?.curatorSubjectAverageScore ?? 0} балл
                                </CardValue>
                            </Box>
                        </Card>

                        {[
                            { range: '0-19', count: examScoreRange?.firstCol },
                            { range: '20-29', count: examScoreRange?.secondCol },
                            { range: '30-39', count: examScoreRange?.thirdCol },
                            { range: '40-50', count: examScoreRange?.fourthCol },
                        ].map((item) => (
                            <Card key={item.range}>
                                <Box>
                                    <CardLabel>{item.range} балл</CardLabel>
                                    <CardValue>{item.count || 0} {formatMessage({ id: 'student' })}</CardValue>
                                </Box>
                            </Card>
                        ))}
                    </Box>
                )}
                {isFetchingStudentAnalysis && loadedPages === 0 ? (
                    <LinearProgress />
                ) : (
                    <>
                        <AnalysisTable
                            examType={examType}
                            studentsData={rows}
                            isFetching={isFetchingStudentAnalysis}
                        />

                        <Sentinel
                            ref={sentinelRef}
                            isLoading={isFetchingStudentAnalysis}
                        />
                    </>
                )}
            </Content>
        </Wrapper >
    )
}

export default AnalysisPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border: 1px solid ${ui.border};
    border-radius: 16px;
    padding: 24px;
    min-height: 80vh;
`

const Card = styled.div`
    border-radius: 14px;
    padding: 16px;
    border: 1px solid ${ui.border};
    background: ${ui.card};
    display: flex;
    gap: 14px;
    align-items: center;
`

const CardLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${ui.muted};
    margin-bottom: 4px;
`

const CardValue = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: ${ui.ink};
`

const CardHint = styled.div`
    font-size: 13px;
    color: ${ui.sub};
    margin-top: 2px;
`

const Header = styled.header`
`

const styles = {
    cards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 2,
        mb: 3,
    },
    buttonGroup: {
        mb: 3,
        display: 'flex',
        minHeight: '40px',
        '& .MuiTabs-indicator': { display: 'none' },
    },
    typeGroup: {
        mb: 2,
        display: 'flex',
        minHeight: '40px',
        '& .MuiTabs-indicator': { display: 'none' },
    },
    // Сегментированный переключатель типа экзамена
    typeButton: (selected: boolean) => ({
        borderRadius: '12px',
        minHeight: '38px',
        padding: '0 18px',
        marginRight: '8px',
        textTransform: 'none',
        fontSize: '13.5px',
        fontWeight: 600,
        backgroundColor: selected ? ui.pink : 'transparent',
        border: `1px solid ${selected ? ui.pink : ui.border}`,
        color: selected ? '#fff' : ui.sub,
        '&:hover': {
            borderColor: ui.pink,
            color: selected ? '#fff' : ui.pink,
        },
    }),
    // Список экзаменов - те же пилюли, что и недели в фильтре
    button: (selected: boolean) => ({
        borderRadius: '12px',
        minHeight: '36px',
        padding: '0 14px',
        marginRight: '8px',
        maxWidth: '320px',
        textTransform: 'none',
        fontSize: '13px',
        fontWeight: selected ? 600 : 400,
        backgroundColor: selected ? ui.pinkSoft : 'transparent',
        border: `1px solid ${selected ? 'transparent' : ui.border}`,
        color: selected ? ui.pink : ui.sub,
        '&:hover': {
            borderColor: ui.pink,
            color: ui.pink,
        },
    }),
}